/**
 * Global hook to check for upcoming virtual appointments and trigger reminders
 * This hook runs at the layout level to show notifications regardless of which page the user is on
 * Shows notification 15 minutes before appointment start time (based on clinic timezone)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  scheduledAppointmentsService,
  ScheduledAppointment,
} from '@/services/features/ScheduledAppointmentsService';

interface UseGlobalAppointmentReminderReturn {
  upcomingAppointments: ScheduledAppointment[];
  showReminder: boolean;
  dismissReminder: () => void;
  isLoading: boolean;
}

const REMINDER_MINUTES_BEFORE = 15;
const CHECK_INTERVAL_MS = 30000; // Check every 30 seconds
const FETCH_INTERVAL_MS = 60000; // Fetch new data every 60 seconds

export const useGlobalAppointmentReminder = (): UseGlobalAppointmentReminderReturn => {
  const { user, hasuraToken } = useAuth();
  const [virtualAppointments, setVirtualAppointments] = useState<ScheduledAppointment[]>([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState<ScheduledAppointment[]>([]);
  const [showReminder, setShowReminder] = useState(false);
  const [dismissedAppointmentIds, setDismissedAppointmentIds] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const fetchIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Determine if user is admin (EA/ED can see all, others see only their own)
  const isAdmin = user?.type === 'EA' || user?.type === 'ED';

  /**
   * Fetch virtual appointments from the API
   */
  const fetchVirtualAppointments = useCallback(async () => {
    if (!hasuraToken || !user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      const userId = parseInt(user.id, 10);
      
      // Fetch a reasonable number of upcoming appointments (no pagination needed for reminders)
      const result = await scheduledAppointmentsService.getVirtualAppointments(
        hasuraToken,
        userId,
        isAdmin,
        50, // Limit to 50 appointments
        0   // No offset
      );
      
      setVirtualAppointments(result.appointments);
    } catch (error) {
      console.error('Error fetching virtual appointments for reminders:', error);
    } finally {
      setIsLoading(false);
    }
  }, [hasuraToken, user?.id, isAdmin]);

  /**
   * Check if an appointment is within the reminder window (15 minutes before start)
   * Uses the appointment's clinic timezone for accurate comparison
   */
  const isWithinReminderWindow = useCallback((appointment: ScheduledAppointment): boolean => {
    try {
      // Parse the appointment start time as UTC
      const appointmentStartUTC = new Date(appointment.appointment_date_start_time);
      
      // Get current time
      const now = new Date();
      
      // Calculate difference in minutes
      const diffMs = appointmentStartUTC.getTime() - now.getTime();
      const diffMinutes = diffMs / (1000 * 60);
      
      // Check if appointment is within the reminder window:
      // - Not yet started (diffMinutes > 0)
      // - Within 15 minutes (diffMinutes <= REMINDER_MINUTES_BEFORE)
      return diffMinutes > 0 && diffMinutes <= REMINDER_MINUTES_BEFORE;
    } catch (error) {
      console.error('Error checking appointment reminder window:', error);
      return false;
    }
  }, []);

  /**
   * Check all virtual appointments and find those that need reminders
   */
  const checkForUpcomingAppointments = useCallback(() => {
    if (!virtualAppointments || virtualAppointments.length === 0) {
      setUpcomingAppointments([]);
      setShowReminder(false);
      return;
    }

    // Filter appointments that are:
    // 1. Within the 15-minute reminder window
    // 2. Not already dismissed by the user
    const appointmentsNeedingReminder = virtualAppointments.filter(
      (appointment) => 
        isWithinReminderWindow(appointment) && 
        !dismissedAppointmentIds.has(appointment.appointment_id)
    );

    if (appointmentsNeedingReminder.length > 0) {
      setUpcomingAppointments(appointmentsNeedingReminder);
      setShowReminder(true);
    } else {
      setUpcomingAppointments([]);
      // Don't auto-hide if user manually dismissed
    }
  }, [virtualAppointments, isWithinReminderWindow, dismissedAppointmentIds]);

  /**
   * Dismiss the reminder popup and track dismissed appointment IDs
   */
  const dismissReminder = useCallback(() => {
    // Add current appointments to dismissed set
    setDismissedAppointmentIds((prev) => {
      const newSet = new Set(prev);
      upcomingAppointments.forEach((apt) => newSet.add(apt.appointment_id));
      return newSet;
    });
    setShowReminder(false);
  }, [upcomingAppointments]);

  // Initial fetch when hook mounts
  useEffect(() => {
    fetchVirtualAppointments();
  }, [fetchVirtualAppointments]);

  // Set up interval to fetch new appointment data periodically
  useEffect(() => {
    fetchIntervalRef.current = setInterval(fetchVirtualAppointments, FETCH_INTERVAL_MS);

    return () => {
      if (fetchIntervalRef.current) {
        clearInterval(fetchIntervalRef.current);
      }
    };
  }, [fetchVirtualAppointments]);

  // Set up interval to check for upcoming appointments
  useEffect(() => {
    // Initial check
    checkForUpcomingAppointments();

    // Set up periodic checking
    checkIntervalRef.current = setInterval(checkForUpcomingAppointments, CHECK_INTERVAL_MS);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [checkForUpcomingAppointments]);

  // Clean up dismissed appointments that are no longer in the list
  useEffect(() => {
    const currentAppointmentIds = new Set(virtualAppointments.map((a) => a.appointment_id));
    setDismissedAppointmentIds((prev) => {
      const newSet = new Set<number>();
      prev.forEach((id) => {
        if (currentAppointmentIds.has(id)) {
          newSet.add(id);
        }
      });
      return newSet;
    });
  }, [virtualAppointments]);

  return {
    upcomingAppointments,
    showReminder,
    dismissReminder,
    isLoading,
  };
};
